import React, { memo } from "react";
import { Typography, Autocomplete, TextField, Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { PropertyMappings, PropertyMappingSelectorProps } from "./PropertyMappingSelector.types";
import { usePropertyMappingSelectorViewModel } from "./PropertyMappingSelector.vm";
import { DefaultImportTableColumns, ImportTableColumn } from "../../../../utilities/types";
import { columnDisplayNames } from "../../../admin/ImportSettings/ImportSettings.types";
import "./PropertyMappingSelector.css";

/** Fields from PropertyMappings that have a corresponding ImportTableColumn */
const PROPERTY_MAPPING_FIELDS: Set<keyof PropertyMappings> = new Set([
	"grower",
	"farm",
	"field",
	"fieldId",
	"labName",
	"labInstructions",
	"submissionNotes",
	"yieldGoal",
	"submitterName",
	"submitterEmail",
	"growerPhoneNumber",
	"eventId",
	"labSubmissionCode",
	"externalId",
	"externalFarmId",
	"externalLabId",
	"dateFieldReadyEstimated",
	"density",
	"depth",
	"frequencyOfMicros",
	"testPackage",
	"fieldPriority",
	"nextCrop",
	"lastCrop",
	"branch",
	"salesperson",
	"salespersonMobilePhone",
]);

/**
 * Generate field configs in the order defined by DefaultImportTableColumns.
 * This ensures property mapping dropdowns match the ImportTable column order.
 */
const FIELD_CONFIGS: Array<{ key: keyof PropertyMappings; label: string }> = (
	Object.keys(DefaultImportTableColumns) as ImportTableColumn[]
)
	.filter((key) => PROPERTY_MAPPING_FIELDS.has(key as keyof PropertyMappings))
	.map((key) => ({ key: key as keyof PropertyMappings, label: columnDisplayNames[key] }));

/**
 * PropertyMappingSelector - Memoized component for mapping GeoJSON properties to job fields.
 * Wrapped with React.memo to prevent unnecessary re-renders when parent re-renders
 * but props haven't changed.
 */
const PropertyMappingSelector = memo(function PropertyMappingSelector(props: PropertyMappingSelectorProps) {
	const vm = usePropertyMappingSelectorViewModel(props);

	// Filter fields based on company visibility settings
	const isFieldVisible = (field: keyof PropertyMappings) => {
		if (field === "uniqueKey") return true;
		const column = field as ImportTableColumn;
		const settings = props.company?.portalSettings?.importTableColumns?.[column];
		return settings?.visible ?? DefaultImportTableColumns[column]?.visible ?? true;
	};
	const visibleFields = FIELD_CONFIGS.filter((field) => isFieldVisible(field.key));

	const renderAutocomplete = (field: keyof PropertyMappings, label: string) => {
		// Find first matching property from the array that exists in this group's properties
		const mappingValue = vm.propertyMappings[field];
		const propertyNames = Array.isArray(mappingValue) ? mappingValue : mappingValue ? [mappingValue] : [];

		// First-match-wins: find the first property name that exists in this group's available properties
		let selectedProperty = null;
		for (const propName of propertyNames) {
			const found = vm.headerProperties?.find((p) => p.name === propName);
			if (found) {
				selectedProperty = found;
				break;
			}
		}

		return (
			<Box>
				<Autocomplete
					options={vm.headerProperties || []}
					value={selectedProperty || null}
					onChange={(_, value) => vm.handleMappingChange(field, value)}
					getOptionLabel={(option) => {
						if (typeof option === "string") return option;
						return option.name;
					}}
					renderOption={({ key, ...props }, option) => (
						<Box component="li" key={key} {...props}>
							<Box>
								<Typography variant="body2">{typeof option === "string" ? option : option.name}</Typography>
								{typeof option === "object" && option.examples && option.examples.length > 0 && (
									<Box>
										{option.examples.map((example, index) => (
											<Typography key={index} variant="caption" color="text.secondary" className="option-example">
												{example}
											</Typography>
										))}
									</Box>
								)}
							</Box>
						</Box>
					)}
					renderInput={(params) => <TextField {...params} label={label} size="small" />}
					clearOnEscape
					clearText="Clear selection"
					isOptionEqualToValue={(option, value) => {
						if (typeof option === "string" && typeof value === "string") return option === value;
						if (typeof option === "object" && typeof value === "string") return option.name === value;
						if (typeof option === "string" && typeof value === "object") return option === value.name;
						if (typeof option === "object" && typeof value === "object") return option.name === value.name;
						return false;
					}}
				/>
				{selectedProperty && selectedProperty.examples && selectedProperty.examples.length > 0 && (
					<Box className="examples-container">
						<Typography variant="caption" color="text.secondary">
							{selectedProperty.examples.slice(0, 3).join(", ")}
						</Typography>
					</Box>
				)}
			</Box>
		);
	};

	return (
		<div className="property-mapping-selector">
			<br />
			<Typography variant="h6" gutterBottom>
				Map File Properties to Columns
			</Typography>
			<Typography variant="body2" color="text.secondary" className="description">
				Select which file properties should populate each column
			</Typography>
			{vm.showAutoGuessNotice && (
				<Typography variant="body2" className="auto-guess-notice">
					We&apos;ve automatically guessed some property mappings for you. Please review and adjust as needed.
				</Typography>
			)}
			<Grid container spacing={2}>
				{visibleFields.map((field) => (
					<Grid size={{ xs: 12, md: 6, lg: 3 }} key={field.key}>
						{renderAutocomplete(field.key, field.label)}
					</Grid>
				))}
			</Grid>
		</div>
	);
});

export default PropertyMappingSelector;
